import { DAY_BOUNDARY_TIME_ZONE } from '@kiao-voice/core'

/**
 * 帳務相關的時間一律以台灣日曆日顯示：`currentPeriodEnd` 是一個 UTC 時間點，
 * 台北 00:00～08:00 之間結束的週期，UTC 日期會比台北早一天。
 */

/** `YYYY/MM/DD`（Asia/Taipei）；缺值或解析失敗回「—」。 */
export function formatTaipeiDate(iso: string | null): string {
  if (!iso) return '—'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: DAY_BOUNDARY_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date)
  const value = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? ''
  return `${value('year')}/${value('month')}/${value('day')}`
}

/** 月／日的自然語句（額度提醒用），同樣釘在台灣時間。 */
export function formatTaipeiMonthDay(iso: string | null, locale: string): string {
  if (!iso) return '—'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  if (locale.toLowerCase().startsWith('zh')) {
    const parts = new Intl.DateTimeFormat('zh-TW', {
      timeZone: DAY_BOUNDARY_TIME_ZONE,
      month: 'numeric',
      day: 'numeric',
    }).formatToParts(date)
    const value = (type: Intl.DateTimeFormatPartTypes) =>
      parts.find((part) => part.type === type)?.value ?? ''
    return `${value('month')} 月 ${value('day')} 日`
  }
  return new Intl.DateTimeFormat('en', {
    timeZone: DAY_BOUNDARY_TIME_ZONE,
    month: 'long',
    day: 'numeric',
  }).format(date)
}

const WEEKDAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const

/**
 * The weekday of a `YYYY-MM-DD` TAIPEI day, as an i18n key.
 *
 * Parsed as UTC midnight and read in UTC: the string already IS a Taipei
 * calendar day, so converting it through a timezone again would shift it. A
 * bare `new Date('2026-09-22')` is UTC midnight by spec, but `getDay()` would
 * then read it in the viewer's zone — a different weekday for anyone west of
 * Greenwich.
 *
 * These three live here rather than in `users-admin/format` because the usage
 * charts moved to `features/admin/` once they had a second caller, and a shared
 * component reaching back into one feature's format module is the dependency
 * pointing the wrong way. This module is already where the Taipei calendar
 * rules live.
 */
export function weekdayKey(date: string): string {
  const parsed = new Date(`${date}T00:00:00.000Z`)
  if (Number.isNaN(parsed.getTime())) return WEEKDAY_KEYS[0]
  return WEEKDAY_KEYS[parsed.getUTCDay()]
}

/** Whether a `YYYY-MM-DD` Taipei day falls on a weekend (shaded on the charts). */
export function isWeekend(date: string): boolean {
  const key = weekdayKey(date)
  return key === 'sat' || key === 'sun'
}

/** `09/22` — the compact form the chart axis and the detail table share. */
export function shortDate(date: string): string {
  return date.slice(5).replace('-', '/')
}
